import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api";
import { Scanner } from "@yudiel/react-qr-scanner";
import { DEFAULT_PRELEVEMENT_ENTITE, PRELEVEMENT_ENTITES } from "./prelevementEntites";

import { useI18n } from "../i18n/I18nContext";

function normalizeText(text) {
  return text.toString().toLowerCase().normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, " ").trim();
}

export default function PrelevementDecoupe() {
  const { t, ui } = useI18n();

  const navigate = useNavigate();

  const [operator, setOperator]         = useState("");
  const [entite, setEntite]             = useState(DEFAULT_PRELEVEMENT_ENTITE);
  const [pns, setPns]                   = useState([]);
  const [pn, setPn]                     = useState("");
  const [pnSearch, setPnSearch]         = useState("");
  const [showPnList, setShowPnList]     = useState(false);
  const [qtyPlacage, setQtyPlacage]     = useState("");
  const [modelDecoupe, setModelDecoupe] = useState(null);
  const [qtyNok, setQtyNok]             = useState("");
  const [qtyNokDouble, setQtyNokDouble] = useState("");
  const [qtyNokTriple, setQtyNokTriple] = useState("");
  const [qtyPliSimple, setQtyPliSimple] = useState(0);
  const [qtyPliDouble, setQtyPliDouble] = useState(0);
  const [qtyPliTriple, setQtyPliTriple] = useState(0);
  const [fentes, setFentes]             = useState("");
  const [noeud, setNoeud]               = useState("");
  const [moisissure, setMoisissure]     = useState("");
  const [decoloration, setDecoloration] = useState("");
  const [autres, setAutres]             = useState("");
  const [now, setNow]                   = useState(new Date());
  const [loadingPn, setLoadingPn]       = useState(false);
  const [sending, setSending]           = useState(false);
  const [error, setError]               = useState("");
  const [success, setSuccess]           = useState("");
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const pnBoxRef = useRef(null);

  useEffect(() => {
    const raw = localStorage.getItem("user");
    if (raw) { try { setOperator(JSON.parse(raw).username || ""); } catch (e) { console.error(e); } }
  }, []);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    function handleClickOutside(e) { if (pnBoxRef.current && !pnBoxRef.current.contains(e.target)) setShowPnList(false); }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => { document.removeEventListener("mousedown", handleClickOutside); document.removeEventListener("touchstart", handleClickOutside); };
  }, []);

  useEffect(() => {
    async function loadPn() {
      setLoadingPn(true); setError("");
      try {
        const res  = await apiFetch("/api/list");
        if (!res.ok) throw new Error("Erreur API");
        const data = await res.json();
        const allPn = Array.isArray(data) ? data.map((item) => item.PN || item.pn).filter(Boolean) : [];
        setPns(Array.from(new Set(allPn)));
      } catch (err) { console.error(err); setError("Impossible de charger la liste des PN."); }
      finally { setLoadingPn(false); }
    }
    loadPn();
  }, []);

  useEffect(() => {
    const total = (Number(fentes) || 0) + (Number(noeud) || 0) + (Number(moisissure) || 0) + (Number(decoloration) || 0) + (Number(autres) || 0);
    if (modelDecoupe === "1") setQtyNok(total);
    else if (modelDecoupe === "3") setQtyNokTriple(total);
  }, [fentes, noeud, moisissure, decoloration, autres, modelDecoupe]);

  useEffect(() => {
    if (modelDecoupe === "1") { setQtyNokDouble(0); setQtyNokTriple(0); }
    else if (modelDecoupe === "2") { setQtyNokTriple(0); }
    else if (modelDecoupe === "3") { setQtyNok(0); setQtyNokDouble(0); }
  }, [modelDecoupe]);

  useEffect(() => {
    const qPlac = Number(qtyPlacage) || 0;
    const qNokS = Number(qtyNok) || 0;
    const qNokD = Number(qtyNokDouble) || 0;
    const qNokT = Number(qtyNokTriple) || 0;
    let sT = 0, dT = 0, tT = 0;
    if (modelDecoupe === "1") { sT = qPlac * 7; }
    else if (modelDecoupe === "2") { sT = qPlac * 1; dT = qPlac * 3; }
    else if (modelDecoupe === "3") { tT = qPlac * 3; }
    setQtyPliSimple(Math.max(sT - qNokS, 0));
    setQtyPliDouble(Math.max(dT - qNokD, 0));
    setQtyPliTriple(Math.max(tT - qNokT, 0));
  }, [qtyPlacage, modelDecoupe, qtyNok, qtyNokDouble, qtyNokTriple]);

  const filteredPns = pns.filter((p) => {
    const s = normalizeText(pnSearch);
    return !s || normalizeText(p).includes(s);
  });

  async function handleSubmit(e) {
    e.preventDefault(); setError(""); setSuccess("");
    if (!pn || !qtyPlacage || !modelDecoupe) { setError("Merci de remplir PN, quantité de placage et modèle de découpe."); return; }
    const payload = {
      operator,
      entite,
      pn,
      qtyPlacage: Number(qtyPlacage) || 0, modelDecoupe,
      qtyNok: Number(qtyNok) || 0, qtyNokDouble: Number(qtyNokDouble) || 0, qtyNokTriple: Number(qtyNokTriple) || 0,
      qtyPliSimple: Number(qtyPliSimple) || 0, qtyPliDouble: Number(qtyPliDouble) || 0, qtyPliTriple: Number(qtyPliTriple) || 0,
      fentes: Number(fentes) || 0, noeud: Number(noeud) || 0, moisissure: Number(moisissure) || 0,
      decoloration: Number(decoloration) || 0, autres: Number(autres) || 0,
      timestamp: new Date().toISOString(),
    };
    try {
      setSending(true);
      const res = await apiFetch("/api/production/prelevement", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.message || "Erreur serveur"); }
      setSuccess("✅ Données enregistrées avec succès.");
    } catch (err) { setError("❌ " + (err.message || "Erreur lors de l'envoi.")); }
    finally { setSending(false); }
  }

  const timeStr = now.toLocaleTimeString("fr-FR");
  const dateStr = now.toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });

  return (
    <div className="pd-page">
      <div className="pd-card">

        {/* HEADER */}
        <div className="pd-header">
          <span>Prélèvement & Découpe</span>
          <span className="pd-header-sub">Contrôle qualité des placages</span>
        </div>

        {/* NAV */}
        <div className="pd-nav">
          <button type="button" className="pd-btn-back" onClick={() => navigate("/menu")}>← Retour menu</button>
          <div className="pd-clock">
            <div className="pd-clock-time">{timeStr}</div>
            <div className="pd-clock-date">{dateStr}</div>
          </div>
        </div>

        {/* OPÉRATEUR */}
        <div className="pd-operator">
          <span className="pd-operator-dot" />
          {operator ? operator.toUpperCase() : "OPÉRATEUR NON CONNECTÉ"}
        </div>

        <form onSubmit={handleSubmit} className="pd-form">

          {/* ENTITÉ */}
          <div className="pd-section">
            <div className="pd-section-label">🏢 Entité qui prélève</div>
            <div className="pd-entite-row">
              {PRELEVEMENT_ENTITES.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  className={`pd-entite-btn ${entite === opt ? "pd-entite-active" : ""}`}
                  onClick={() => setEntite(opt)}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          {/* PN SELECTOR */}
          <div className="pd-section">
            <div className="pd-section-label pd-label-green">🌲 Parc Number — Lot à prélever</div>
            <div ref={pnBoxRef} style={{ position: "relative" }}>
              <div className="pd-select-row">
                <input value={pnSearch}
                  onChange={(e) => { setPnSearch(e.target.value); setShowPnList(true); }}
                  onFocus={() => setShowPnList(true)}
                  placeholder={loadingPn ? "Chargement des PN…" : "Filtrer ou sélectionner un PN"}
                  disabled={loadingPn}
                  className="pd-select-input" />
                <button type="button" className="pd-scan-btn" onClick={() => setIsScannerOpen(true)} title="Scanner QR">📷</button>
              </div>
              {pn && (
                <div className="pd-selected-badge">
                  <span>✔</span><strong>{pn}</strong>
                  <button type="button" className="pd-clear-btn" onClick={() => { setPn(""); setPnSearch(""); }}>✕</button>
                </div>
              )}
              {showPnList && filteredPns.length > 0 && (
                <div className="pd-dropdown" onMouseDown={(e) => e.preventDefault()}>
                  {filteredPns.map((p) => (
                    <div key={p} className="pd-dropdown-item"
                      onClick={() => { setPn(p); setPnSearch(p); setShowPnList(false); }}>{p}</div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* QUANTITÉ PLACAGE */}
          <div className="pd-section pd-section-red">
            <div className="pd-section-label pd-label-red">📦 Quantité de placage à prélever</div>
            <input type="number" min="0" value={qtyPlacage}
              onChange={(e) => setQtyPlacage(e.target.value)}
              className="pd-qty-input" placeholder="Ex: 50" />
          </div>

          {/* MODÈLE DÉCOUPE */}
          <div className="pd-section">
            <div className="pd-section-label">✂️ Modèle de découpe</div>
            <div className="pd-grid-3">
              <button type="button"
                className={`pd-decoupe-btn ${modelDecoupe === "1" ? "pd-decoupe-active" : ""}`}
                onClick={() => setModelDecoupe("1")}>
                <span className="pd-decoupe-num">1</span>
                <span className="pd-decoupe-desc">7 plis simples<br/>2250 × 70 mm</span>
              </button>
              <button type="button"
                className={`pd-decoupe-btn ${modelDecoupe === "2" ? "pd-decoupe-active" : ""}`}
                onClick={() => setModelDecoupe("2")}>
                <span className="pd-decoupe-num">2</span>
                <span className="pd-decoupe-desc">3 plis doubles 2250 × 160 mm<br/>+ 1 pli simple 2250 × 70 mm</span>
              </button>
              <button type="button"
                className={`pd-decoupe-btn ${modelDecoupe === "3" ? "pd-decoupe-active" : ""}`}
                onClick={() => setModelDecoupe("3")}>
                <span className="pd-decoupe-num">3</span>
                <span className="pd-decoupe-desc">3 plis triples<br/>2250 × 360 mm</span>
              </button>
            </div>
          </div>

          {/* DÉFAUTS */}
          <div className="pd-section pd-section-defauts">
            <div className="pd-section-label pd-label-yellow">⚠️ Défauts observés</div>
            <div className="pd-defauts-grid">
              <FieldNumber label="Fentes"                    value={fentes}       onChange={setFentes} />
              <FieldNumber label="Nœud"                      value={noeud}        onChange={setNoeud} />
              <FieldNumber label="Moisissure"                value={moisissure}   onChange={setMoisissure} />
              <FieldNumber label="Décoloration"              value={decoloration} onChange={setDecoloration} />
              <FieldNumber label="Autres"                    value={autres}       onChange={setAutres} />
            </div>
          </div>

          {/* RÉSULTATS */}
          <div className="pd-section pd-section-results">
            <div className="pd-section-label">📊 Récapitulatif</div>
            <div className="pd-results-grid">
              <div className="pd-result-group pd-result-nok">
                <div className="pd-result-title">❌ NOK</div>
                <FieldNumber label="Pli simple NOK" value={qtyNok}       onChange={setQtyNok} disabled={modelDecoupe === "3"} />
                <FieldNumber label="Pli double NOK" value={qtyNokDouble} onChange={setQtyNokDouble} disabled={modelDecoupe === "1" || modelDecoupe === "3"} />
                <FieldNumber label="Pli triple NOK" value={qtyNokTriple} onChange={setQtyNokTriple} disabled={modelDecoupe !== "3"} />
              </div>
              <div className="pd-result-group pd-result-ok">
                <div className="pd-result-title">✅ OK</div>
                <FieldNumber label="Pli simple OK" value={qtyPliSimple} onChange={() => {}} disabled />
                <FieldNumber label="Pli double OK" value={qtyPliDouble} onChange={() => {}} disabled />
                <FieldNumber label="Pli triple OK" value={qtyPliTriple} onChange={() => {}} disabled />
              </div>
            </div>
          </div>

          {/* MESSAGES */}
          {error   && <div className="pd-msg pd-msg-error">{error}</div>}
          {success && <div className="pd-msg pd-msg-success">{success}</div>}

          {/* SUBMIT */}
          <div className="pd-submit-row">
            <button type="submit" disabled={sending} className="pd-btn-submit">
              {sending ? "⏳ Envoi en cours…" : "✔ OK confirmation & envoi"}
            </button>
            <button type="button" className="pd-btn-time">
              Time
              <div className="pd-btn-time-sub">{timeStr}</div>
            </button>
          </div>

        </form>
      </div>

      {/* SCANNER */}
      {isScannerOpen && (
        <div className="pd-modal-backdrop" onClick={() => setIsScannerOpen(false)}>
          <div className="pd-modal-scanner" onClick={(e) => e.stopPropagation()}>
            <div className="pd-modal-title">📷 Scanner un QR code PN</div>
            <div style={{ borderRadius: 10, overflow: "hidden" }}>
              <Scanner
                onScan={(codes) => {
                  if (!codes?.length) return;
                  const text = codes[0]?.rawValue;
                  if (!text) return;
                  setPn(text); setPnSearch(text); setShowPnList(false); setIsScannerOpen(false);
                }}
                onError={console.error}
                constraints={{ facingMode: "environment" }}
              />
            </div>
            <button type="button" className="pd-btn-close-scanner" onClick={() => setIsScannerOpen(false)}>✕ Fermer</button>
          </div>
        </div>
      )}

      <Style />
    </div>
  );
}

/* ── FIELD NUMBER ── */
function FieldNumber({ label, value, onChange, disabled = false }) {
  return (
    <div className={`pd-field-row ${disabled ? "pd-field-disabled" : ""}`}>
      <div className="pd-field-label">{label}</div>
      <input type="number" min="0" value={value} disabled={disabled}
        onChange={(e) => onChange && onChange(e.target.value)}
        className="pd-field-input" />
    </div>
  );
}

/* ── STYLES ── */
function Style() {
  return (
    <style>{`
      *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

      .pd-page {
        min-height: 100vh; width: 100%;
        background: radial-gradient(ellipse at top,#01283a 0%,#010F14 70%);
        display: flex; justify-content: center; align-items: flex-start;
        padding: 16px; font-family: 'Inter','Segoe UI',sans-serif;
      }
      .pd-card {
        width: 100%; max-width: 640px;
        background: #011e2b; border: 1px solid #1a4a5a;
        border-radius: 16px; overflow: hidden;
        box-shadow: 0 8px 40px rgba(0,0,0,0.6), 0 0 0 1px #0a3040;
      }

      /* HEADER */
      .pd-header {
        background: linear-gradient(135deg,#E6FF3E,#c8e800);
        color: #000; padding: 14px 20px;
        display: flex; flex-direction: column; align-items: center; gap: 2px;
      }
      .pd-header span  { font-size: 15px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.1em; }
      .pd-header-sub   { font-size: 11px; font-weight: 500; opacity: 0.6; }

      /* NAV */
      .pd-nav {
        background: #010f14; padding: 8px 16px;
        display: flex; justify-content: space-between; align-items: center;
        border-bottom: 1px solid #1a4a5a;
      }
      .pd-btn-back {
        background: transparent; color: #7ab8cc;
        border: 1px solid #1a4a5a; border-radius: 6px;
        padding: 5px 10px; font-size: 11px; font-weight: 600; cursor: pointer; transition: all 0.2s;
      }
      .pd-btn-back:hover { background: #1a4a5a; color: #fff; }
      .pd-clock         { text-align: right; }
      .pd-clock-time    { color: #E6FF3E; font-size: 15px; font-weight: 700; font-variant-numeric: tabular-nums; }
      .pd-clock-date    { color: #5a8a99; font-size: 10px; }

      /* OPÉRATEUR */
      .pd-operator {
        display: flex; align-items: center; gap: 10px;
        background: linear-gradient(90deg,#012836,#013d50);
        border: 1px solid #1a5a6a; border-radius: 10px;
        margin: 14px 20px 0; padding: 12px 16px;
        color: #fff; font-size: 14px; font-weight: 700; letter-spacing: 0.06em;
      }
      .pd-operator-dot { width: 10px; height: 10px; background: #00B050; border-radius: 50%; box-shadow: 0 0 6px #00B050; flex-shrink: 0; }

      /* FORM */
      .pd-form { padding: 16px 20px 24px; display: flex; flex-direction: column; gap: 16px; overflow-y: auto; flex: 1; }

      /* SECTIONS */
      .pd-section {
        background: #012030; border: 1px solid #1a4a5a;
        border-radius: 12px; padding: 14px 16px;
        display: flex; flex-direction: column; gap: 12px;
      }
      .pd-section-red      { border-color: #C00000; background: #1a0808; }
      .pd-section-defauts  { border-color: #FFD966; background: #1a1400; }
      .pd-section-results  { border-color: #7ab8cc; background: #011820; }
      .pd-section-label    { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #7ab8cc; }
      .pd-entite-row {
        display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px;
      }
      .pd-entite-btn {
        background: #012030; color: #7ab8cc; border: 1px solid #1a4a5a;
        border-radius: 8px; padding: 10px 8px; font-size: 13px; font-weight: 700;
        cursor: pointer; transition: all 0.15s;
      }
      .pd-entite-btn:hover { background: #1a4a5a; color: #fff; }
      .pd-entite-active {
        background: #E6FF3E; color: #000; border-color: #E6FF3E;
      }
      .pd-label-green      { color: #7aff9a; }
      .pd-label-yellow     { color: #FFD966; }
      .pd-label-red        { color: #ff9090; }

      /* SELECT ROW */
      .pd-select-row   { display: flex; gap: 8px; align-items: center; }
      .pd-select-input {
        flex: 1; background: #003818; color: #7aff9a;
        border: 1px solid #005020; border-radius: 8px;
        padding: 10px 14px; font-size: 14px; font-weight: 600;
        outline: none; transition: border 0.2s;
      }
      .pd-select-input:focus { border-color: #00B050; box-shadow: 0 0 0 2px rgba(0,176,80,0.15); }
      .pd-select-input:disabled { opacity: 0.6; cursor: not-allowed; }
      .pd-scan-btn {
        width: 46px; height: 46px; flex-shrink: 0; background: #012030;
        border: 1px solid #1a4a5a; border-radius: 8px;
        font-size: 20px; cursor: pointer; transition: background 0.2s;
      }
      .pd-scan-btn:hover { background: #1a4a5a; }
      .pd-selected-badge {
        display: flex; align-items: center; gap: 8px; margin-top: 6px;
        background: #002810; border: 1px solid #00B050; border-radius: 8px;
        padding: 5px 12px; font-size: 12px; color: #7aff9a;
      }
      .pd-selected-badge strong { flex: 1; font-size: 13px; }
      .pd-clear-btn   { background: transparent; border: none; color: #ff8080; cursor: pointer; font-size: 14px; font-weight: 700; }
      .pd-dropdown    {
        position: absolute; left: 0; right: 0; margin-top: 4px;
        max-height: 200px; overflow-y: auto;
        background: #002818; border: 1px solid #005020; border-radius: 8px; z-index: 50;
        box-shadow: 0 8px 24px rgba(0,0,0,0.5);
      }
      .pd-dropdown-item {
        padding: 8px 14px; color: #aaffcc; font-size: 13px;
        cursor: pointer; border-bottom: 1px solid #003818; transition: background 0.15s;
      }
      .pd-dropdown-item:hover { background: #003d20; }

      /* QTY INPUT */
      .pd-qty-input {
        width: 100%; max-width: 200px; align-self: center;
        background: #010f14; color: #fff; border: 2px solid #C00000;
        border-radius: 8px; padding: 12px; font-size: 22px; font-weight: 700;
        text-align: center; outline: none; transition: border 0.2s;
      }
      .pd-qty-input:focus { border-color: #ff6060; box-shadow: 0 0 0 2px rgba(192,0,0,0.2); }

      /* DÉCOUPE BUTTONS */
      .pd-grid-2       { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
      .pd-grid-3       { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
      .pd-decoupe-btn  {
        display: flex; flex-direction: column; align-items: center; gap: 6px;
        padding: 16px 10px; border-radius: 12px; border: 2px solid #FF8800;
        background: transparent; color: #fff; cursor: pointer; transition: all 0.2s;
      }
      .pd-decoupe-btn:hover    { background: rgba(255,136,0,0.15); }
      .pd-decoupe-active       { background: #FF8800 !important; color: #000 !important; box-shadow: 0 0 0 3px rgba(230,255,62,0.5); }
      .pd-decoupe-active .pd-decoupe-num  { color: #000; }
      .pd-decoupe-active .pd-decoupe-desc { color: #000; }
      .pd-decoupe-num  { font-size: 28px; font-weight: 900; color: #FF8800; line-height: 1; }
      .pd-decoupe-desc { font-size: 12px; font-weight: 600; text-align: center; line-height: 1.4; color: #aaa; }

      /* DÉFAUTS GRID */
      .pd-defauts-grid { display: flex; flex-direction: column; gap: 10px; }

      /* RESULTS */
      .pd-results-grid   { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
      .pd-result-group   { display: flex; flex-direction: column; gap: 10px; background: #010f14; border-radius: 10px; padding: 12px; border: 1px solid #1a4a5a; }
      .pd-result-nok     { border-color: #C00000; }
      .pd-result-ok      { border-color: #00B050; }
      .pd-result-title   { font-size: 13px; font-weight: 800; text-align: center; letter-spacing: 0.05em; color: #fff; }
      .pd-result-nok .pd-result-title { color: #ff9090; }
      .pd-result-ok  .pd-result-title { color: #7aff9a; }

      /* FIELD NUMBER */
      .pd-field-row    { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
      .pd-field-label  { flex: 1; color: #fff; font-weight: 600; font-size: 13px; }
      .pd-field-input  {
        width: 100px; background: #012836; color: #fff;
        border: 1px solid #3a6a7a; border-radius: 8px;
        padding: 8px 10px; font-size: 14px; font-weight: 700; text-align: center;
        outline: none; transition: border 0.2s;
      }
      .pd-field-input:focus   { border-color: #E6FF3E; }
      .pd-field-disabled .pd-field-label { opacity: 0.5; }
      .pd-field-disabled .pd-field-input { background: #1a2a2a; color: #7ab8cc; border-style: dashed; cursor: not-allowed; opacity: 0.7; }

      /* MESSAGES */
      .pd-msg         { border-radius: 8px; padding: 10px 14px; font-size: 13px; font-weight: 600; }
      .pd-msg-error   { background: #2a0808; border: 1px solid #C00000; color: #ff9090; }
      .pd-msg-success { background: #002810; border: 1px solid #00B050; color: #80ff90; }

      /* SUBMIT */
      .pd-submit-row  { display: flex; gap: 10px; align-items: center; }
      .pd-btn-submit  {
        flex: 1; background: linear-gradient(135deg,#00B050,#008a3c);
        color: #000; border: none; border-radius: 10px;
        padding: 14px; font-size: 15px; font-weight: 800;
        text-transform: uppercase; letter-spacing: 0.08em; cursor: pointer;
        transition: opacity 0.2s, transform 0.1s;
        box-shadow: 0 4px 16px rgba(0,176,80,0.3);
      }
      .pd-btn-submit:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
      .pd-btn-submit:disabled { opacity: 0.5; cursor: not-allowed; }
      .pd-btn-time {
        min-width: 110px; background: #C00000; color: #fff;
        border: 2px solid #7F0000; border-radius: 8px;
        padding: 8px 10px; font-weight: 700; font-size: 12px; cursor: default; text-align: center;
      }
      .pd-btn-time-sub { font-size: 11px; margin-top: 2px; font-variant-numeric: tabular-nums; }

      /* SCANNER MODAL */
      .pd-modal-backdrop {
        position: fixed; inset: 0; background: rgba(0,0,0,0.8);
        display: flex; justify-content: center; align-items: center;
        z-index: 999; backdrop-filter: blur(4px);
      }
      .pd-modal-scanner {
        width: 90%; max-width: 420px; background: #011e2b;
        border: 1px solid #00B050; border-radius: 14px; padding: 16px;
        display: flex; flex-direction: column; gap: 12px;
        box-shadow: 0 0 40px rgba(0,176,80,0.2);
      }
      .pd-modal-title { color: #fff; font-size: 14px; font-weight: 700; text-align: center; }
      .pd-btn-close-scanner {
        background: #C00000; color: #fff; border: none; border-radius: 8px;
        padding: 10px; font-weight: 700; font-size: 13px; cursor: pointer; transition: opacity 0.2s;
      }
      .pd-btn-close-scanner:hover { opacity: 0.85; }

      /* TABLETTE */
      @media (min-width: 768px) {
        .pd-card  { max-width: 800px; }
        .pd-header span { font-size: 18px; }
        .pd-form  { padding: 20px 28px 28px; gap: 20px; }
        .pd-select-input { font-size: 16px; padding: 12px 16px; }
        .pd-scan-btn     { width: 54px; height: 54px; font-size: 24px; }
        .pd-qty-input    { font-size: 28px; max-width: 260px; }
        .pd-decoupe-num  { font-size: 36px; }
        .pd-decoupe-desc { font-size: 13px; }
        .pd-field-label  { font-size: 14px; }
        .pd-field-input  { width: 120px; font-size: 16px; }
        .pd-btn-submit   { font-size: 16px; padding: 16px; }
      }

      /* DESKTOP */
      @media (min-width: 1024px) {
        .pd-card  { max-width: 900px; }
        .pd-header span { font-size: 20px; }
        .pd-form  { padding: 20px 36px 36px; gap: 22px; }
        .pd-operator { margin: 16px 36px 0; font-size: 16px; }
        .pd-section  { padding: 18px 20px; border-radius: 14px; }
        .pd-section-label { font-size: 13px; }
        .pd-select-input  { font-size: 18px; padding: 14px 18px; }
        .pd-qty-input     { font-size: 32px; max-width: 280px; padding: 14px; }
        .pd-decoupe-btn   { padding: 20px 14px; border-radius: 14px; }
        .pd-decoupe-num   { font-size: 42px; }
        .pd-decoupe-desc  { font-size: 14px; }
        .pd-results-grid  { gap: 16px; }
        .pd-result-group  { padding: 16px; }
        .pd-field-label   { font-size: 15px; }
        .pd-field-input   { width: 130px; font-size: 17px; padding: 10px; }
        .pd-btn-submit    { font-size: 18px; padding: 18px; border-radius: 12px; }
        .pd-btn-time      { min-width: 140px; }
      }

      /* XXL */
      @media (min-width: 1400px) {
        .pd-page  { padding: 32px; }
        .pd-card  { max-width: 1100px; border-radius: 20px; }
        .pd-header { padding: 24px 48px; }
        .pd-header span { font-size: 26px; letter-spacing: 0.14em; }
        .pd-header-sub  { font-size: 14px; }
        .pd-nav   { padding: 14px 36px; }
        .pd-clock-time  { font-size: 22px; }
        .pd-operator    { margin: 18px 48px 0; font-size: 20px; padding: 16px 24px; border-radius: 14px; }
        .pd-operator-dot { width: 14px; height: 14px; }
        .pd-form  { padding: 24px 48px 40px; gap: 28px; }
        .pd-section { padding: 22px 24px; border-radius: 16px; }
        .pd-section-label { font-size: 15px; letter-spacing: 0.1em; }
        .pd-select-input  { font-size: 22px; padding: 16px 20px; border-radius: 10px; height: 62px; }
        .pd-scan-btn      { width: 66px; height: 66px; font-size: 28px; border-radius: 10px; }
        .pd-dropdown-item { font-size: 15px; padding: 10px 18px; }
        .pd-qty-input     { font-size: 38px; max-width: 320px; padding: 16px; border-radius: 10px; }
        .pd-grid-2, .pd-grid-3 { gap: 18px; }
        .pd-decoupe-btn   { padding: 24px 18px; border-radius: 16px; }
        .pd-decoupe-num   { font-size: 52px; }
        .pd-decoupe-desc  { font-size: 16px; }
        .pd-results-grid  { gap: 20px; }
        .pd-result-group  { padding: 20px; border-radius: 12px; }
        .pd-result-title  { font-size: 15px; }
        .pd-field-label   { font-size: 17px; }
        .pd-field-input   { width: 150px; font-size: 20px; padding: 12px; border-radius: 10px; }
        .pd-msg           { font-size: 15px; padding: 14px 18px; border-radius: 10px; }
        .pd-btn-submit    { font-size: 22px; padding: 22px; border-radius: 14px; letter-spacing: 0.1em; }
        .pd-btn-time      { min-width: 170px; font-size: 14px; padding: 14px; border-radius: 10px; }
        .pd-btn-time-sub  { font-size: 13px; }
      }
    `}</style>
  );
}
